import React, { createContext, useCallback, useContext, useState } from 'react';
import Toast, { ToastType } from '../components/Toast';

type ToastItem = {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
} | null;

type ToastContextType = {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [toast, setToast] = useState<ToastItem>(null);

  const showToast = useCallback(
    (message: string, type: ToastType = 'success', duration = 3000) => {
      if (!message) return;
      const id = Date.now();
      setToast({ id, message, type, duration });
    },
    []
  );

  // Called when the toast disappears or is dismissed
  const handleToastClose = () => setToast(null);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && (
        <Toast
          key={toast.id} // important: remount Toast on new toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={handleToastClose} // notify provider to clear toast
        />
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
